// A player drawn from a session the client simulates itself: the player's own
// character in third person, the ghost of a run and a watched replay. The
// animation's game side (timers, events, movetype) is fed by the session's
// animation state after every move, like pmove does in ET; without the player
// model bundle it is a box.

import * as THREE from 'three';
import type { AnimState } from '../shared/game/movetype';
import type { Vec3 } from '../shared/math';
import { EF_PRONE, PMF_DUCKED, type PlayerState } from '../shared/physics/pmove';
import { PlayerModel, type PlayerRenderKit } from './playerModel';
import { playAnimEvent } from './remotePlayers';

export type ModelStyle = 'solid' | 'ghost';

export class SessionModel {
  readonly object = new THREE.Group();
  private model: PlayerModel | null = null;
  private readonly box: THREE.Mesh;
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private eventSeq = 0;
  private lastRender = 0;

  constructor(
    private readonly kit: PlayerRenderKit | null,
    private readonly style: ModelStyle,
    private readonly clientNum = 0,
  ) {
    const ghost = style === 'ghost';
    const bodyGeo = new THREE.BoxGeometry(36, 36, 72);
    const bodyMat = new THREE.MeshBasicMaterial({ color: ghost ? 0x66ccff : 0xffa040, transparent: true, opacity: ghost ? 0.28 : 0.45, depthWrite: false });
    this.box = new THREE.Mesh(bodyGeo, bodyMat);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(bodyGeo), new THREE.LineBasicMaterial({ color: ghost ? 0x9fe0ff : 0xffc070, transparent: true, opacity: 0.8 }));
    this.box.add(edges);
    this.object.add(this.box);
    this.box.visible = !kit;
    this.materials.push(bodyMat, edges.material as THREE.Material);
    this.geometries.push(bodyGeo, edges.geometry);
    this.reset(0);
  }

  /** A fresh character: the animation starts over (a new run, a respawn). */
  reset(eventSeq: number): void {
    this.model?.dispose();
    this.model = null;
    if (this.kit) {
      this.model = new PlayerModel(this.kit, this.clientNum, this.style === 'ghost');
      this.object.add(this.model.object);
    }
    this.eventSeq = eventSeq;
    this.lastRender = 0;
  }

  /** After each move of the session: the game side of the animation. */
  tick(anim: AnimState, msec: number): void {
    const model = this.model;
    if (!model) return;
    model.animator.advanceTimers(msec);
    if (anim.eventSeq !== this.eventSeq) {
      this.eventSeq = anim.eventSeq;
      playAnimEvent(model, anim.event);
    }
    model.animator.locomotion(anim.moveType, anim.moveFallback);
  }

  /**
   * @param origin interpolated player origin
   * @param snapshotTime changes when the session moved (a counter is fine)
   */
  render(ps: PlayerState, anim: AnimState, origin: Vec3, angles: Vec3, snapshotTime: number, now: number, viewOrigin: ArrayLike<number>): void {
    const crouched = (ps.pmFlags & PMF_DUCKED) !== 0;
    const prone = (ps.eFlags & EF_PRONE) !== 0;
    if (this.model) {
      const frametime = this.lastRender ? Math.max(0, now - this.lastRender) : 0;
      this.lastRender = now;
      this.object.position.set(0, 0, 0);
      this.model.render({ time: now, frametime, origin, angles: [angles[0], angles[1], 0], velocity: ps.velocity, movementDir: anim.movementDir, prone, crouched, snapshotTime, viewOrigin });
      return;
    }
    const height = prone ? 40 : crouched ? 48 : 72;
    this.box.scale.set(1, 1, height / 72);
    // the box spans mins.z (-24) .. maxs.z
    this.object.position.set(origin[0], origin[1], origin[2] - 24 + height / 2);
  }

  dispose(): void {
    this.model?.dispose();
    this.model = null;
    this.materials.forEach((m) => m.dispose());
    this.geometries.forEach((g) => g.dispose());
  }
}
